/**
 * The one-tap status menu — "שינוי סטטוס מהיר".
 *
 * A manager standing at the gate does not open a form to say that a soldier
 * went home; they tap the badge on the roster row and pick a word. This module
 * is the single source of truth for what those words are and what each one
 * writes, so the sheet stays a list of buttons and the healthcheck can exercise
 * the decision without a browser.
 *
 * Two kinds of option, and the difference matters:
 *
 * - **Standing** (זמין, לא זמין) — writes `Soldier.availability` and clears any
 *   timed status. This is where the soldier falls back to.
 * - **Timed** (בבית, במשימה, בגימלים…) — writes `Soldier.temporaryStatus` alone
 *   and leaves the standing availability exactly as it was, so when the status
 *   lapses the soldier returns to it with nothing to run. The deadline is the
 *   next 08:00 in unit time; a manager who needs another hour sets it in the
 *   full status dialog.
 *
 * The deadline is optional. With `indefinite`, the sheet writes the status with
 * no end date at all and it stays in force until a commander changes it — the
 * sentence it reads as is {@link formatTimedStatus}'s business. A manager who
 * does not know when the soldier is back must still be able to say where they
 * are.
 *
 * Nothing here touches the matching engine — it keeps reading
 * `Soldier.availability`, exactly as {@link effectiveAvailability} describes.
 */
import {
  AVAILABILITY_ICONS,
  TIMED_STATUS_ICONS,
  effectiveAvailability,
  isTimedStatusActive,
  nextMorningDeadline,
} from './soldier-status';
import { SOLDIER_AVAILABILITY_LABELS, TIMED_STATUS_LABELS } from '../types/fleet';
import type { Soldier, SoldierAvailability, TimedStatus, TimedStatusKind } from '../types/fleet';

/** One button on the sheet. */
export interface QuickSoldierStatus {
  /** Stable key — the availability or the timed kind it writes. */
  id: string;
  label: string;
  icon: string;
  /** The standing availability it writes, or `null` for a timed option. */
  availability: SoldierAvailability | null;
  /** The timed status it opens, or `null` for a standing option. */
  kind: TimedStatusKind | null;
}

/** The order the sheet lists the timed statuses in — commonest first. */
const TIMED_ORDER: readonly TimedStatusKind[] = [
  'home',
  'mission',
  'sick',
  'leave',
  'course',
  'guard',
];

/**
 * The menu, in the order it is painted: זמין leads it because that is the
 * answer given most often, and לא זמין closes it.
 */
export const QUICK_SOLDIER_STATUSES: readonly QuickSoldierStatus[] = [
  {
    id: 'available',
    label: SOLDIER_AVAILABILITY_LABELS.available,
    icon: AVAILABILITY_ICONS.available,
    availability: 'available',
    kind: null,
  },
  ...TIMED_ORDER.map((kind) => ({
    id: kind,
    label: TIMED_STATUS_LABELS[kind],
    icon: TIMED_STATUS_ICONS[kind],
    availability: null,
    kind,
  })),
  {
    id: 'unavailable',
    label: SOLDIER_AVAILABILITY_LABELS.unavailable,
    icon: AVAILABILITY_ICONS.unavailable,
    availability: 'unavailable',
    kind: null,
  },
];

/** The option under a given key, or `null` when nothing answers to it. */
export function findQuickStatus(id: string | null | undefined): QuickSoldierStatus | null {
  return QUICK_SOLDIER_STATUSES.find((option) => option.id === id) ?? null;
}

/** Exactly what one tap writes — the two status fields, and nothing else. */
export interface QuickStatusPatch {
  availability: SoldierAvailability;
  temporaryStatus: TimedStatus | null;
}

/** How one tap is written when the manager has no end date to give. */
export interface QuickStatusOptions {
  /** Writes the status with no deadline — it runs until changed by hand. */
  indefinite?: boolean;
}

/**
 * The patch for one tap. Pure, and the only place the rule lives: a standing
 * option clears the timed status, a timed option keeps the standing one, and
 * `indefinite` decides whether the timed one carries a deadline at all.
 */
export function quickStatusPatch(
  soldier: Soldier,
  option: QuickSoldierStatus,
  now: number = Date.now(),
  { indefinite = false }: QuickStatusOptions = {},
): QuickStatusPatch {
  if (option.kind === null) {
    return {
      availability: option.availability ?? soldier.availability,
      temporaryStatus: null,
    };
  }
  return {
    availability: soldier.availability,
    temporaryStatus: {
      kind: option.kind,
      until: indefinite ? null : nextMorningDeadline(now),
    },
  };
}

/**
 * True when the soldier already reads as that option — the sheet marks it, so
 * the current status is visible before anything is tapped.
 */
export function isQuickStatusCurrent(
  soldier: Soldier,
  option: QuickSoldierStatus,
  now: number = Date.now(),
): boolean {
  const timed = isTimedStatusActive(soldier.temporaryStatus, now);
  if (option.kind !== null) return timed && soldier.temporaryStatus?.kind === option.kind;
  return !timed && effectiveAvailability(soldier, now) === option.availability;
}
